<template>
  <div>
    <div class="d-flex mb-3 align-items-end">
      <h1 class="h4 primary--text">
        {{ $t('navigation.orders') }}
      </h1>
      <div class="ml-auto">
        <download-excel
          file-name="order-positions.xlsx"
          :url="`/orders/positions-xlsx`"
          :filters="filters"
          :params="params"
        />
        <v-btn
          outlined
          large
          @click="toggleFilters"
        >
          <v-icon left>
            mdi-filter-variant
          </v-icon>
          {{ $t('common.filter') }}
        </v-btn>
      </div>
    </div>
    <table-filters
      v-if="isFilterShown"
      :filters="filters"
      @search="getItems(true)"
    />
    <v-data-table
      class="row-clickable"
      fixed-header
      :headers="headers"
      :items="items"
      item-key="id"
      :footer-props="{
        itemsPerPageOptions: [10, 20, 50, 100],
      }"
      :options="orderOptions"
      :server-items-length="total"
      :loading="loading"
      :loading-text="$t('common.loading_data')"
      @click:row="goTo"
      @update:options="updateOptions"
    />
  </div>
</template>

<script>
import TableFilters from '@/components/common/TableFilters';
import DownloadExcel from '@/components/common/DownloadExcel';
import { orderStatuses } from '@/utils/enums';
import { mapState, mapGetters } from 'vuex';

export default {
  name: 'Orders',
  components: {
    TableFilters,
    DownloadExcel,
  },
  data() {
    return {
      loaded: false,
      total: 0,
      items: [],
      loading: false,
      options: {
        page: 1,
        sortBy: ['date'],
        sortDesc: [true],
      },
      headers: [
        {
          text: this.$t('common.placement_date'),
          value: 'date',
        },
        {
          text: this.$t('common.document_number'),
          value: 'number',
        },
        {
          text: this.$t('common.order_number'),
          value: 'dealerInternalOrder',
        },
        // {
        //   text: this.$t('common.article'),
        //   value: 'parts.code',
        // },
        {
          text: this.$t('common.number_of_items'),
          value: 'numberOfitems',
        },
        {
          text: this.$t('common.order_type'),
          value: 'orderType',
        },
        {
          text: this.$t('common.status'),
          value: 'status',
        },
        {
          text: this.$t('common.status_date'),
          value: 'statusDate',
        },
        {
          text: this.$t('common.comment'),
          value: 'comments',
        },
        {
          text: this.$t('common.order_dealers_price_vat'),
          //value: 'parts.sumWithVAT',
          value: 'totalWithVAT',
        },
        {
          text: 'VIN',
          value: 'VIN',
        },
        {
          text: this.$t('common.originator'),
          value: 'createdBy',
        },
      ],
      isFilterShown: false,
      filters: [{
        label: this.$t('common.date'),
        key: 'date',
        type: 'date',
        value: '',
      }, {
        label: this.$t('common.document_number'),
        key: 'number',
        value: '',
      }, {
        label: this.$t('common.order_number'),
        key: 'dealerInternalOrder',
        value: '',
      }, {
        label: this.$t('common.status'),
        key: 'status',
        value: '',
        type: 'select',
        options: orderStatuses
          .map((status) => ({ value: status, text: this.$t(`enums.order_statuses.${status.toLowerCase()}`) })),
      }, {
        label: this.$t('common.total_price_with_vat'),
        key: 'totalWithVAT',
        type: 'number',
        value: '',
      }, {
        label: this.$t('common.originator'),
        key: 'createdBy',
        value: '',
      }, {
        label: this.$t('common.article'),
        key: 'parts.code',
        value: '',
      }, {
        label: 'VIN',
        key: 'VIN',
        value: '',
      }],
    };
  },
  watch: {
    filters: {
      deep: true,
      handler(nv){
        this.$store.commit('setOrdersQuery', nv);
      }
    }
  },
  computed: {
    ...mapState(['workMode', 'orderOptions']),
    ...mapGetters(['orderParams']),
    params() {
      const {
        sortBy, sortDesc, page, itemsPerPage,
      } = this.orderOptions;

      const params = {
        populate: 'createdBy',
      };

      params.query = this.filters.reduce((acc, { key, value, type }) => {
        if (!value) {
          return acc;
        }

        if (type === 'number') {
          acc[key] = +value;
          return acc;
        }

        acc[key] = {
          $regex: `.*${value}.*`,
          $options: 'i',
        };

        if (key === 'status' && value === 'notPaid') {
          acc[key] = { $ne: 'Closed' };
        }

        return acc;
      }, {});

      if (!!itemsPerPage) {
        params.pageSize = itemsPerPage;
        params.page = page;
      }

      if (sortBy && sortBy.length) {
        params.sort = `${sortDesc[0] ? '-' : ''}${sortBy[0]}`;
      }
      return params;
    },
  },
  mounted(){
    // small vuetify hach
    this.$nextTick(() => this.loaded = true);
  },
  created() {
    this.setFilterValues()
    this.setWorkModeLayout();
  },
  methods: {
    setFilterValues(){
      // set values from curent query
      const keys = Object.keys(this.orderOptions.query)
      for(const key of keys){
        if (key !== '$expr'){
          const value = this.orderOptions.query[key]['$regex'].replaceAll('.*', '');
          if(value){
            const idx = this.filters.findIndex(f => f.key === key);
            if (idx > -1){
              this.filters[idx].value = value;
              this.isFilterShown = true;
            }
          }
        } else {
          // TODO fix date restore
          // const str = JSON.stringify(this.orderOptions.query['$expr'])
          // const value = str.replaceAll('.*', '');
          // if(value){
          //   const idx = this.filters.findIndex(f => f.key === 'date');
          //   if (idx > -1){
          //     this.filters[idx].value = value;
          //     this.isFilterShown = true;
          //   }
          // }
        }
      }
    },
    setOptions(opt){
      this.$store.commit('setOrdersOptions', opt)
    },
    updateOptions(opt){
      if (this.loaded){
        this.setOptions(opt);
      }
      this.getItems();
    },
    setWorkModeLayout() {
      if (this.workMode.isModeSollers) {
        ['orderType', 'VIN']
          .forEach((tblItem) => {
            this.headers.splice(this.headers.findIndex((item) => item.value === tblItem), 1);
          });
      }
    },
    async getItems(filtered) {
      this.loading = true;
      if (filtered) {
        const opt = Object.assign(this.orderOptions, { page: 1 })
        this.setOptions(opt);
      }
      const { data } = await this.$http.get('orders/positionsa', { params: this.orderParams }).finally(() => {
        this.loading = false;
      });

      if (data.rows.length) {
        this.items = data.rows.map((it) => ({
          ...it,
          statusDate: this.getDate(it.statusDate, 'L', 'Europe/Moscow'),
          date: this.getDate(it.date, 'L', 'Europe/Moscow'),
          status: it.status && this.$t(`enums.order_statuses.${it.status.toLowerCase()}`),
          orderType: it.orderType && this.$t(`enums.order_types.${it.orderType}`),
          dueDate: this.getDate(it.dueDate),
          orderPartId: `${it.id}-${it.parts.code}`,
          numberOfitems: it.parts.length,
          id: it._id,
          totalWithVAT: this.$numberFormatter(it.totalWithVAT),
          createdBy: it.createdBy.login
        }));
      } else {
        this.items = data.rows;
      }

      this.total = data.total;
    },
    toggleFilters() {
      this.isFilterShown = !this.isFilterShown;
    },
    goTo({ id }) {
      console.log({ id })
      this.$router.push(`/orders/${id}`);
    },

    getDate(date, format = 'LLL', tz) {
      if (!date) { return ''; }

      const mDate = tz ? this.$moment.tz(date, tz) : this.$moment(date);

      return ((new Date(date)).toISOString() !== '1970-01-01T00:00:00.000Z')
        ? mDate.format(format) : '';
    },
  },
};
</script>

<style lang="scss">
.row-clickable tr {
  cursor: pointer;
}
</style>
